<template>
  <div class="serviceplace-information mt-1">
    <validation-observer ref="formValidator">
      <div class="license-disciplines-title mx-4">
        Seleccione la(s) Disciplina(s) de Licencia(s) a las que desea añadir
        Lugar(es) de Servicio(s)
      </div>
      <div
        v-for="licenseDisciplines in specialistLicenseDisciplines"
        :key="licenseDisciplines.specialistLicenseId"
        class="row mx-4"
      >
        <div class="col-12 mx-3 license-disciplines-container">
          <div class="license-label">
            {{ licenseDisciplines.licenseSpecialtyName }}
          </div>
          <div class="mt-1 disciplines-container">
            <div
              v-for="discipline in licenseDisciplines.disciplines"
              :key="discipline.id"
            >
              <check-box
                v-model="discipline.selected"
                class="discipline-checkbox"
                :text="discipline.name"
              />
            </div>
          </div>
        </div>
      </div>
      <div class="row mx-4">
        <error
          v-if="!hasValidDiscipline"
          class="col-12 mx-3"
          :message="validationMessages.disciplines"
          variant="no-border"
        />
      </div>
      <div class="row mx-4">
        <div class="col mb-3">
          <validation-provider
            v-slot="{ errors }"
            name="LocationType"
            rules="required"
          >
            <custom-drop-down
              v-model="selectedLocationType"
              :custom-error-msg="validationMessages.locationType"
              :error="errors[0]"
              fieldkey="id"
              fieldname="name"
              label="Tipo de Lugar:"
              :options="locationTypes"
              placeholder="Seleccione"
              :reduce="(o) => ({ id: o.id, name: o.name })"
              @input="
                () => {
                  selectedCompanyLocation = null;
                  selectedMunicipality = null;
                  companyLocations = [];
                }
              "
            />
          </validation-provider>
        </div>
        <div class="col mb-3">
          <validation-provider
            v-slot="{ errors }"
            name="Municipality"
            rules="required"
          >
            <custom-drop-down
              v-model="selectedMunicipality"
              :custom-error-msg="validationMessages.municipality"
              :disabled="selectedLocationType == null"
              :error="errors[0]"
              fieldkey="id"
              fieldname="name"
              label="Municipio:"
              :options="municipalities"
              placeholder="Seleccione"
              :reduce="(o) => ({ id: o.id, name: o.name })"
              @input="
                () => {
                  selectedCompanyLocation = null;
                  companyLocations = [];
                }
              "
            />
          </validation-provider>
        </div>
        <div class="col-sm-2 col-md-2 mb-3">
          <validation-provider
            v-slot="{ errors }"
            name="Location"
            rules="required"
          >
            <custom-drop-down
              v-model="selectedCompanyLocation"
              :custom-error-msg="validationMessages.location"
              :disabled="selectedMunicipality == null"
              :error="errors[0]"
              fieldkey="id"
              fieldname="name"
              label="Lugar de Servicio:"
              :options="companyLocations"
              placeholder="Seleccione"
            />
          </validation-provider>
        </div>
        <div class="col-sm-12 col-md-1 mb-3">
          <check-box
            v-model="specialistLocation.canAssignSpecialistFlag"
            class="vertical-center"
            text="Admisión"
          />
        </div>
        <div v-if="selfAssign" class="col-sm-12 col-md-1 ml-3 mb-3">
          <check-box
            v-model="specialistLocation.selfAssign"
            class="vertical-center"
            text="Asignar"
          />
        </div>
        <div class="col mb-3 p-0 action-buttons">
          <action-button
            class="py-2 m-2 btnCancel"
            i="fas fa-times-circle"
            text="Cancelar"
            variant="outline-danger"
            @click="cancel"
          />
          <action-button
            class="py-2 m-2 float-right"
            i="fas fa-save"
            text="Guardar"
            variant="success"
            @click="save"
          />
        </div>
      </div>
      <error
        v-if="customErrorMessage != null"
        :message="customErrorMessage"
        variant="no-border"
      />
      <div
        v-if="hasServiceLocationValidations"
        class="col-sm-12 col-md-12 mb-3 pt-1 pl-4 pb-3"
      >
        <error
          v-for="validation in serviceLocationValidations"
          :key="validation.message"
          :message="validation.message"
          variant="no-border"
        />
      </div>
    </validation-observer>
  </div>
</template>

<script>
import CustomDropDown from '@/components/common/form/CustomDropDown';
import {
  getSpecialistLicenseDisciplines,
  createServicePlace,
  editServicePlace,
  getCompanyLocationsCurrentYear,
  getSpecialtyDisciplines,
  getMunicipalityNameByLocationFiscalSpecialist,
  getServicePlaces,
  getCompanyMunicipalities,
} from '@/modules/provider/api';

import visitRegistryApi from '@/modules/visitRegistry/api.js';
import Error from '@/components/common/Error';
import alert from '@/mixins/alert.mixin';
import { mapState } from 'vuex';
export default {
  name: 'ServicePlaceInformationAddEdit',
  components: {
    Error,
    CustomDropDown,
  },
  mixins: [alert],
  model: {
    prop: 'location',
    event: 'on-saved',
  },
  props: {
    location: {
      type: Object,
      default: () => ({
        specialistLocationId: null,
        specialistId: 0,
        locationFiscalYearId: 0,
        locationTypeName: null,
        municipalityName: null,
        municipality: null,
        locationName: null,
        canAssignSpecialistFlag: false,
        selfAssign: false,
        disciplines: [],
      }),
    },
    immediateSave: {
      type: Boolean,
      required: true,
    },
    //ECG:I = insert mode, E = edit mode, crear objeto congelado con esto?
    opMode: {
      type: String,
      default: () => '',
    },
    customErrorMessage: {
      type: String,
      default: () => null,
    },
    specialistId: {
      type: Number,
      default: () => null,
    },
    unsavedLicenseData: {
      type: Array,
      default: () => [],
    },
    companyId: {
      type: Number,
      default: () => 0,
    },
    fiscalYearId: {
      type: Number,
      default: () => 0,
    },
    locations: {
      type: Array,
      default: () => [],
    },
    selfAssign: {
      type: Boolean,
      required: false,
    },
    canAddLocations: {
      type: Boolean,
      required: false,
    },
  },
  data() {
    return {
      specialistLocation: { ...this.location },
      specialistLicenseDisciplines: [],
      locationTypes: [],
      municipalities: [],
      companyLocations: [''],
      selectedLocationType: null,
      selectedMunicipality: null,
      selectedCompanyLocation: null,
      validationMessages: {
        locationType: 'Favor de seleccionar un Tipo de Lugar.',
        location: 'Favor de seleccionar un Lugar de Servicio.',
        licenses: 'Favor de seleccionar la(s) licencia(s).',
        municipality: 'Favor de seleccionar un municipio.',
        disciplines:
          'Favor de seleccionar la(s) Disciplina(s) de Licencia(s) a las que desea añadir Lugar(es) de Servicio(s).',
      },
      serviceLocationValidations: [],
      hasValidDiscipline: true,
    };
  },
  computed: {
    ...mapState('$_user', ['user']),
    hasServiceLocationValidations() {
      return this.serviceLocationValidations.some(
        (item) => item !== null && item !== undefined
      );
    },
  },
  watch: {
    selectedLocationType(newValue) {
      let self = this;
      self.specialistLocation.locationTypeId = newValue.id;
      self.specialistLocation.locationTypeName = newValue.name;
      getCompanyMunicipalities({
        fiscalYearId: self.fiscalYearId,
        companyId: self.companyId,
        locationTypeId: self.specialistLocation.locationTypeId,
      }).then((data) => {
        this.municipalities = data;
      });
    },
    selectedMunicipality(newValue) {
      let self = this;
      self.specialistLocation.municipality = newValue;
      getServicePlaces({
        fiscalYearId: self.fiscalYearId,
        companyId: self.companyId,
        locationTypeId: self.selectedLocationType
          ? self.selectedLocationType.id
          : null,
        municipalityId: newValue?.id,
      }).then((resp) => {
        self.companyLocations = resp.data;
      });
    },
    selectedCompanyLocation(newValue) {
      this.specialistLocation.locationFiscalYearId = newValue.id;
      this.specialistLocation.locationName = newValue.name;
    },
  },
  async created() {
    let self = this;
    this.locationTypes = await visitRegistryApi.getLocationsType();
    if (self.immediateSave) {
      getSpecialistLicenseDisciplines(self.specialistId).then((resp) => {
        self.specialistLicenseDisciplines = self.setSelectedDisciplines(
          resp.data
        );
      });
    } else {
      let specialtyIds = self.unsavedLicenseData.map((l) => {
        return { specialtyId: l.specialtyTypeId, licenseId: l.tempId };
      });
      getSpecialtyDisciplines(specialtyIds).then((resp) => {
        self.specialistLicenseDisciplines = self.setSelectedDisciplines(
          resp.data
        );
      });
    }
  },
  mounted() {
    if (this.specialistLocation.locationTypeId) {
      this.selectedLocationType = {
        id: this.specialistLocation.locationTypeId,
        name: this.specialistLocation.locationTypeName,
      };
      getCompanyLocationsCurrentYear(
        this.specialistLocation.locationTypeId,
        this.companyId
      ).then((resp) => {
        let toRemove =
          self.locations.length == 0
            ? []
            : resp.data.filter(
                (x) =>
                  self.locations.filter(
                    (z) =>
                      z.locationName
                        .toLocaleUpperCase()
                        .indexOf(x.name.toLocaleUpperCase()) > -1
                  ).length > 0
              );
        self.companyLocations = resp.data.filter(function(el) {
          return toRemove.every((d) => el.id !== d.id);
        });
      });
    }
    if (this.specialistLocation.locationFiscalYearId) {
      this.selectedCompanyLocation = {
        id: this.specialistLocation.locationFiscalYearId,
        name: this.specialistLocation.locationName,
      };
    }
    if (this.specialistLocation.municipality) {
      this.selectedMunicipality = this.specialistLocation.municipality;
    }
  },
  methods: {
    async save() {
      let self = this;

      this.hasValidDiscipline = this.specialistLicenseDisciplines.some((x) =>
        x.disciplines.some((e) => e.selected)
      );
      const isValid =
        (await self.$refs.formValidator.validate()) && this.hasValidDiscipline;

      if (isValid) {
        self.specialistLocation.companyId = self.companyId;
        self.specialistLocation.fiscalYearId = self.fiscalYearId;
        await self.syncSelectedDisciplines(self.specialistLocation);
        if (self.immediateSave) {
          let promise = null;

          if (self.opMode == 'E') {
            promise = editServicePlace(self.specialistLocation);
          } else if (self.opMode == 'I') {
            this.$emit('init-add-locations');
            if (!this.canAddLocations) {
              this.ShowToast(
                'Error',
                'Es necesario completar los documentos de Dispensa previamente.',
                'error'
              );
              return;
            }

            promise = createServicePlace(self.specialistLocation);
          }

          await promise
            .then((res) => {
              if (res.data.validations.length > 0) {
                this.serviceLocationValidations = res.data.validations;
                return;
              }

              self.ShowToast(
                'Proceso Exitoso',
                'Lugar de Servicio guardado exitosamente.',
                'success'
              );

              self.specialistLocation = res.data;
              self.$emit('on-saved', self.specialistLocation);
              if (self.opMode == 'I') this.$emit('finish-add-location');
            })
            .catch((error) => {
              self.ShowToast('Error', error.response.data.message, 'error');
            });
        } else {
          self.$emit('on-saved', self.specialistLocation);
        }
      } else {
        //TODO: Use the stored one
        self.ShowToast('Error', 'Información incompleta. (190803)', 'error');
      }
    },
    setSelectedDisciplines(licenseDisciplines) {
      licenseDisciplines.forEach((l) =>
        l.disciplines.forEach(
          (d) =>
            (d.selected = this.specialistLocation.disciplines.some(
              (ld) =>
                ld.disciplineTypeId === d.id &&
                ld.specialistLicenseId == l.specialistLicenseId
            ))
        )
      );
      return licenseDisciplines;
    },
    async syncSelectedDisciplines(loc) {
      let disc =
        //ECG:Busco las disciplinas seleccionadas de todas las licensias para asignarlas a la localizacion
        this.specialistLicenseDisciplines.reduce(
          (acc, cur) =>
            acc.concat(
              cur.disciplines
                .filter((d) => d.selected === true)
                .map((d) => {
                  return {
                    disciplineTypeId: d.id,
                    specialistLicenseId: cur.specialistLicenseId,
                    specialtyTypeId: cur.licenseSpecialtyId,
                  };
                })
            ),
          []
        );
      loc.disciplines = disc;

      const { data } = await getMunicipalityNameByLocationFiscalSpecialist(
        loc.locationFiscalYearId
      );
      loc.municipalityName = data;
    },
    async cancel() {
      this.$emit('on-cancel');
    },
  },
};
</script>
<style scoped>
.vertical-center {
  position: absolute;
  top: 50%;
}

.action-buttons {
  text-align: -webkit-right;
  top: 20px;
  min-width: 24%;
  margin-left: 2%;
  padding-left: 0 !important;
}

.license-disciplines-container {
  border: 1px solid #e2e2e2;
  border-radius: 5px;
  background-color: white;
  padding: 9px 14px 14px 23px;
  font: 14px/1.5rem Lato;
  margin-bottom: 8px;
}

.license-disciplines-container .license-label {
  color: #3f5b60;
  font-weight: bold;
}

.disciplines-container .discipline-checkbox {
  font-weight: normal;
  color: #727272;
}

.license-disciplines-title {
  margin-left: 24px;
  padding: 7px 16px;
  font-weight: normal;
  color: #727272;
}

@media (min-width: 576px) {
  .serviceplace-information .header {
    padding-left: 0.25 !important;
    padding-right: 0.25 !important;
    padding-top: 10px;
  }

  .serviceplace-information .body {
    padding-left: 0.43 !important;
    padding-right: 0.43 !important;
  }

  .serviceplace-information .btnCancel {
    float: left;
  }
}

@media (min-width: 768px) {
  .serviceplace-information .header {
    padding-left: 0.33rem !important;
    padding-right: 0.33rem !important;
  }

  .serviceplace-information .body {
    padding-left: 1rem !important;
    padding-right: 1rem !important;
  }

  .serviceplace-information .btnCancel {
    float: inherit;
  }
}

@media (min-width: 992px) {
  .serviceplace-information .header {
    padding-left: 0.66rem !important;
    padding-right: 0.66rem !important;
  }

  .serviceplace-information .body {
    padding-left: 1.75rem !important;
    padding-right: 1.75rem !important;
  }
}

@media (min-width: 1200px) {
  .serviceplace-information .header {
    padding-left: 1rem !important;
    padding-right: 1rem !important;
  }

  .serviceplace-information .body {
    padding-left: 3rem !important;
    padding-right: 3rem !important;
  }
}

.checkbox {
  margin-top: 33px !important;
  margin-left: 35%;
}
@media (max-width: 768px) {
  .checkbox {
    margin-top: 15px !important;
    margin-left: 20%;
  }
}
</style>
